import {BIOMARKER_STATUS_COLORS} from '@/shared/constants/colors';
import type {BiomarkerStatus, BiomarkerZone, CustomBiomarkerConfig} from '@/shared/types/biomarker';
import type {BandColourKey, BandSeverity, BiomarkerDisplayRule, DisplayBand} from './displayRuleTypes';

const COLOUR_KEYS = {
    normal: BIOMARKER_STATUS_COLORS.NORMAL,
    optimal_deep: BIOMARKER_STATUS_COLORS.OPTIMAL_DEEP,
    borderline: BIOMARKER_STATUS_COLORS.LOW,
    critical: BIOMARKER_STATUS_COLORS.HIGH,
} as const;

const SEVERITY_COLORS = {
    normal: BIOMARKER_STATUS_COLORS.NORMAL,
    borderline: BIOMARKER_STATUS_COLORS.LOW,
    critical: BIOMARKER_STATUS_COLORS.HIGH,
} as const;

/** A band's colour: its explicit key when set, else derived from severity. */
export function bandColour(band: {severity: BandSeverity; colour_key: BandColourKey | null}): string {
    if (band.colour_key) return COLOUR_KEYS[band.colour_key];
    return SEVERITY_COLORS[band.severity];
}

/**
 * Best-effort mapping from a band's free-text label (and, as a fallback, its
 * severity) onto the legacy BiomarkerStatus enum. `status` is legacy plumbing
 * only — the actual colour and tier a band renders with come from `severity`
 * and `colour_key` via the `finish()` override in classify.ts, not from this
 * mapping. It still matters for two things classify.ts derives from `status`
 * rather than `severity`: `resolveColors()`'s low/normal/high bucket lookup,
 * and `optimalZoneInfo`, which keys off `status === 'Normal' || 'Optimal'`.
 */
function mapLabelToStatus(label: string, severity: BandSeverity): BiomarkerStatus {
    const l = label.toLowerCase();
    if (l.includes('optimal')) return 'Optimal';
    if (l.includes('normal') || l.includes('in range')) return 'Normal';
    if (l.includes('borderline')) return 'Borderline';
    if (l.includes('elevated')) return 'Elevated';
    if (l.includes('deficient')) return 'Deficient';
    if (l.includes('toxic')) return 'Toxic';
    if (l.includes('positive')) return 'Positive';
    if (l.includes('negative')) return 'Negative';
    if (l.includes('low') || l.includes('below')) return 'Low';
    if (l.includes('high') || l.includes('above')) return 'High';
    switch (severity) {
        case 'critical':
            return 'High';
        case 'borderline':
            return 'Borderline';
        case 'normal':
        default:
            return 'Normal';
    }
}

/**
 * A band's min_value/max_value can be null on the first/last band only
 * (validateDisplayRule enforces this) — "unbounded low" / "unbounded high".
 * classify.ts's zone maths needs plain numbers on both ends of every zone, so
 * the null end is replaced with a finite sentinel far outside the rule's own
 * bounded range: 1000x the width spanned by the rule's finite band edges,
 * below the lowest / above the highest. No real lab value comes within
 * three orders of magnitude of a bounded range this way, so the sentinel is
 * unreachable in practice — classification always resolves inside a real
 * band rather than the below-first/above-last fallback branches in
 * classifyByConfigZones.
 */
const SENTINEL_SPAN_MULTIPLIER = 1000;

function computeSentinels(bands: DisplayBand[]): {min: number; max: number} {
    const finiteBounds = bands.flatMap((b) => [b.min_value, b.max_value]).filter((v): v is number => v !== null);
    const lo = Math.min(...finiteBounds);
    const hi = Math.max(...finiteBounds);
    const span = Math.max(hi - lo, 1);
    return {
        min: lo - span * SENTINEL_SPAN_MULTIPLIER,
        max: hi + span * SENTINEL_SPAN_MULTIPLIER,
    };
}

/** Adapts a banded rule to CustomBiomarkerConfig. Returns null for value_only
 *  rules and for rules with no bands. */
export function ruleToConfig(rule: BiomarkerDisplayRule): CustomBiomarkerConfig | null {
    if (rule.display_mode === 'value_only') return null;
    if (rule.bands.length === 0) return null;

    const bands = [...rule.bands].sort((a, b) => a.sort_order - b.sort_order);
    const sentinels = computeSentinels(bands);

    const zones: BiomarkerZone[] = bands.map((band) => ({
        label: band.label,
        min: band.min_value ?? sentinels.min,
        max: band.max_value ?? sentinels.max,
        percentage: band.proportion,
        color: bandColour(band),
        status: mapLabelToStatus(band.label, band.severity),
        severity: band.severity,
    }));

    return {
        name: rule.display_name,
        zoneCount: zones.length as 2 | 3 | 4,
        zones,
    };
}

/** Reference text for a value_only rule, else null. */
export function valueOnlyReferenceText(rule: BiomarkerDisplayRule | null): string | null {
    if (!rule || rule.display_mode !== 'value_only') return null;
    return rule.reference_text;
}
